import { useState } from "react";
import {
  sectionStyle,
  buttonStyle,
  sectionCardStyle,
  buttonRowStyle,
  fieldGroupStyle,
} from "../style";
import { parseNumericInput, minmaxTest } from "./inputerUtils";
import { useEffect } from "react";

export function AxisInput({
  axis,
  setAxis,
  minValue,
  maxValue,
  setMinValue,
  setMaxValue,
  axisName,
  showRange = false,
  onLogAction,
}) {
  const handleFieldBlur = (field, value) => {
    onLogAction?.("axis_setting_changed", { axisName, field, value });
  };

  function changeLog(value) {
    if (value) {
      let max = maxValue[axisName];
      if (max <= 0) {
        max = 100;
        setMaxValue({
          ...maxValue,
          [axisName]: Number(100),
        });
      }
      if (minValue[axisName] <= 0) {
        setMinValue({
          ...minValue,
          [axisName]: Number(max / 100),
        });
      }
    }
    setAxis((prev) => ({ ...prev, isLog: value }));
    onLogAction?.("axis_log_toggled", { axisName, value });
  }
  return (
    <div style={sectionCardStyle}>
      <div style={{ fontWeight: 600 }}>
        {axisName === "x" ? "x軸" : "y軸"}設定
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <div style={fieldGroupStyle}>
          <div>{axisName}軸変数</div>
          <input
            value={axis[axisName]}
            onChange={(e) => {
              const nextValue = e.target.value;
              setAxis((prev) => ({ ...prev, [axisName]: nextValue }));
            }}
            onBlur={(e) => handleFieldBlur(axisName, e.target.value)}
          />
        </div>

        <div style={fieldGroupStyle}>
          <div>{axisName}軸ラベル</div>
          <input
            value={axis.label}
            onChange={(e) => {
              const nextValue = e.target.value;
              setAxis((prev) => ({ ...prev, label: nextValue }));
            }}
            onBlur={(e) => handleFieldBlur("label", e.target.value)}
          />
        </div>

        <div style={fieldGroupStyle}>
          <div>{axisName}軸単位</div>
          <input
            value={axis.unit}
            onChange={(e) => {
              const nextValue = e.target.value;
              setAxis((prev) => ({ ...prev, unit: nextValue }));
            }}
            onBlur={(e) => handleFieldBlur("unit", e.target.value)}
          />
        </div>
        {showRange && (
          <>
            <div style={fieldGroupStyle}>
              <div>{axisName}最小</div>
              <input
                type="number"
                value={minValue[axisName]}
                onChange={(e) =>
                  setMinValue({
                    ...minValue,
                    [axisName]: Number(e.target.value),
                  })
                }
                onBlur={(e) => handleFieldBlur("min", Number(e.target.value))}
              />
            </div>

            <div style={fieldGroupStyle}>
              <div>{axisName}最大</div>
              <input
                type="number"
                value={maxValue[axisName]}
                onChange={(e) =>
                  setMaxValue({
                    ...maxValue,
                    [axisName]: Number(e.target.value),
                  })
                }
                onBlur={(e) => handleFieldBlur("max", Number(e.target.value))}
              />
            </div>
          </>
        )}
      </div>
      <div style={{ display: "flex" }} onClick={() => changeLog(!axis.isLog)}>
        <input
          type="checkBox"
          checked={axis.isLog}
          onChange={(e) => changeLog(e.target.checked)}
        />
        対数軸を使用
      </div>
    </div>
  );
}

export function LegendInput({
  legendState,
  setLegendState,
  onLogAction,
  maxX,
  maxY,
}) {
  const handleLegendBlur = (field, value) => {
    onLogAction?.("legend_position_changed", { axis: field, value });
  };

  return (
    <div style={sectionCardStyle}>
      <div style={{ fontWeight: 600 }}>凡例</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <label style={fieldGroupStyle}>
          <div>表示</div>
          <input
            type="checkbox"
            checked={legendState.visible !== false}
            onChange={(e) => {
              const nextValue = e.target.checked;
              setLegendState((prev) => ({ ...prev, visible: nextValue }));
              onLogAction?.("legend_visibility_changed", {
                visible: nextValue,
              });
            }}
          />
        </label>
        <div style={fieldGroupStyle}>
          <div>凡例位置x</div>
          <input
            type="number"
            value={legendState.x}
            onChange={(e) =>
              setLegendState((prev) => {
                const nextValue = Number(e.target.value);
                const clampedX = Number.isNaN(nextValue) ? 0 : nextValue;
                return {
                  ...prev,
                  x:
                    maxX != null
                      ? Math.min(Math.max(clampedX, 0), maxX)
                      : Math.max(clampedX, 0),
                };
              })
            }
            onBlur={(e) => handleLegendBlur("x", Number(e.target.value))}
          />
        </div>
        <div style={fieldGroupStyle}>
          <div>凡例位置y</div>
          <input
            type="number"
            value={legendState.y}
            onChange={(e) =>
              setLegendState((prev) => {
                const nextValue = Number(e.target.value);
                const clampedY = Number.isNaN(nextValue) ? 0 : nextValue;
                return {
                  ...prev,
                  y:
                    maxY != null
                      ? Math.min(Math.max(clampedY, 0), maxY)
                      : Math.max(clampedY, 0),
                };
              })
            }
            onBlur={(e) => handleLegendBlur("y", Number(e.target.value))}
          />
        </div>
      </div>
    </div>
  );
}

export function AxisRangeInput({
  minValue,
  maxValue,
  setMinValue,
  setMaxValue,
  axisName,
  onLogAction,
}) {
  const handleRangeBlur = (bound, value) => {
    onLogAction?.("axis_range_changed", { axisName, bound, value });
  };

  const [min,setMin] = useState(null)
  const [max,setMax] = useState(null)

  return (
    <div style={sectionCardStyle}>
      <div style={{ fontWeight: 600 }}>
        {axisName === "x" ? "x軸" : "y軸"}範囲
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <div style={fieldGroupStyle}>
          <div>{axisName}最小</div>
          <input
            type="number"
            value={min ?? minValue[axisName]}
            onChange={(e)=>{setMin(e.target.value)}}
            onBlur={(e) => {
              handleRangeBlur("min", Number(e.target.value))
              setMinValue({
                ...minValue,
                [axisName]: Number(e.target.value),
              })
              setMin(null)
            }}
          />
        </div>
        <div style={fieldGroupStyle}>
          <div>{axisName}最大</div>
          <input
            type="number"
            value={max ?? maxValue[axisName]}
            onChange={(e)=>{setMax(e.target.value)}}
            onBlur={(e) => {
              handleRangeBlur("max", Number(e.target.value))
              setMaxValue({
                ...maxValue,
                [axisName]: Number(e.target.value),
              })
              setMax(null)
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function ZoomInput({ zoomVelosity, setZoomVelosity, onLogAction }) {
  const [value, setValue] = useState(zoomVelosity);

  const handleCommit = (nextValue) => {
    const clampedValue = minmaxTest(nextValue, 1, 20);
    setZoomVelosity(clampedValue);
    setValue(clampedValue);
    onLogAction?.("zoom_setting_changed", { zoomVelosity: clampedValue });
  };

  return (
    <div style={sectionCardStyle}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <div style={fieldGroupStyle}>
          <div>ズーム速度</div>
          <input
            type="number"
            value={value}
            onBlur={(e) => handleCommit(e.target.value)}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleCommit(e.target.value);
                e.target.blur();
              }
            }}
          />
          <input
            type="range"
            min={1}
            max={20}
            step={0.1}
            value={zoomVelosity}
            onChange={(e) => {
              const nextValue = Number(e.target.value);
              setZoomVelosity(minmaxTest(nextValue, 1, 20));
              setValue(nextValue);
              onLogAction?.("zoom_setting_changed", {});
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function UserSettingInput({ zoomVelosity, setZoomVelosity }) {
  return (
    <div style={sectionCardStyle}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <div style={fieldGroupStyle}>
          <div>単位表示</div>
        </div>
      </div>
    </div>
  );
}

export function FontSizeInput({
  axisFontSize,
  setAxisFontSize,
  legendState,
  setLegendState,
  onLogAction,
}) {
  const [axisSize, setAxisSize] = useState(axisFontSize);
  const [legendSize, setLegendSize] = useState(legendState.fontSize);

  const handleAxisCommit = (nextValue) => {
    const clampedValue = minmaxTest(nextValue, 4, 32);
    setAxisFontSize(clampedValue);
    setAxisSize(clampedValue);
    onLogAction?.("font_size_changed", { target: "axis", value: clampedValue });
  };

  const handleLegendCommit = (nextValue) => {
    const clampedValue = minmaxTest(nextValue, 4, 32);
    setLegendState((prev) => ({ ...prev, fontSize: clampedValue }));
    setLegendSize(clampedValue);
    onLogAction?.("font_size_changed", {
      target: "legend",
      value: clampedValue,
    });
  };

  return (
    <div style={sectionCardStyle}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <div style={fieldGroupStyle}>
          <div>軸フォントサイズ</div>
          <input
            type="number"
            value={axisSize}
            onBlur={(e) => handleAxisCommit(e.target.value)}
            onChange={(e) => {
              setAxisSize(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleAxisCommit(e.target.value);
                e.target.blur();
              }
            }}
          />
          <input
            type="range"
            min={4}
            max={32}
            step={1}
            value={axisFontSize}
            onChange={(e) => {
              const nextValue = Number(e.target.value);
              setAxisFontSize(nextValue);
              setAxisSize(nextValue);
              onLogAction?.("font_size_changed", {
                target: "axis",
                value: nextValue,
              });
            }}
          />
        </div>
        <div style={fieldGroupStyle}>
          <div>凡例フォントサイズ</div>
          <input
            type="number"
            value={legendSize}
            onChange={(e) => {
              setLegendSize(e.target.value);
            }}
            onBlur={(e) => handleLegendCommit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleLegendCommit(e.target.value);
                e.target.blur();
              }
            }}
          />
          <input
            type="range"
            min={4}
            max={32}
            step={1}
            value={legendState.fontSize}
            onChange={(e) => {
              const nextValue = Number(e.target.value);
              setLegendState((prev) => ({ ...prev, fontSize: nextValue }));
              setLegendSize(nextValue);
              onLogAction?.("font_size_changed", {
                target: "legend",
                value: nextValue,
              });
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function HeightInput({
  height,
  setHeight,
  onLogAction,
}) {
  const [h, setH] = useState(null);

  const heightCommit = (nextValue) => {
    const clampedValue = minmaxTest(nextValue, 30, 100);
    setHeight(clampedValue);
  };  

  return (
    <div style={sectionCardStyle}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <div style={fieldGroupStyle}>
          <div>グラフサイズ</div>
          <input
            type="number"
            value={h ?? height}
            onChange={(e) => {
              setH(e.target.value);
            }}
            onBlur={(e) => {
              heightCommit(e.target.value)
              setH(null)
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setHeight(e.target.value)
                setH(null)
                e.target.blur();
              }
            }}
          />
          <input
            type="range"
            min={30}
            max={100}
            step={1}
            value={h ?? height}
            onChange={(e) => {
              const nextValue = Number(e.target.value);
              setHeight(e.target.value);
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function DataPointRow({
  point,
  pointIndex,
  dataIndex,
  datas,
  setDatas,
  onLogAction,
}) {
  const handlePointChange = (field, value) => {
    const newData = [...datas];
    const pointData = newData[dataIndex].points;
    pointData[pointIndex][field] = parseNumericInput(value);
    newData[dataIndex] = { ...newData[dataIndex], points: pointData };
    setDatas(newData);
  };

  const handlePointBlur = (field, value) => {
    onLogAction?.("point_updated", {
      dataIndex,
      pointIndex,
      field,
      value: parseNumericInput(value),
    });
  };

  const handleDeletePoint = () => {
    const newData = [...datas];
    const pointData = newData[dataIndex].points;
    newData[dataIndex] = {
      ...newData[dataIndex],
      points: pointData.filter((_, idx) => idx !== pointIndex),
    };
    setDatas(newData);
    onLogAction?.("point_deleted", { dataIndex, pointIndex });
  };

  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}
    >
      <input
        type="number"
        step="any"
        value={point.x}
        onChange={(e) => handlePointChange("x", e.target.value)}
        onBlur={(e) => handlePointBlur("x", e.target.value)}
        style={{ flex: 1 }}
      />
      <input
        type="number"
        step="any"
        value={point.y}
        onChange={(e) => handlePointChange("y", e.target.value)}
        onBlur={(e) => handlePointBlur("y", e.target.value)}
        style={{ flex: 1 }}
      />
      <button
        onClick={handleDeletePoint}
        aria-label="データ点を削除"
        title="データ点を削除"
        style={{
          width: 20,
          height: 20,
          border: "none",
          borderRadius: 6,
          background: "#fff",
          cursor: "pointer",
          fontSize: 18,
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}

export function DataSeriesCard({
  data,
  seriesIndex,
  datas,
  setDatas,
  onLogAction,
}) {
  const handleDataSeriesChange = (field, value) => {
    const newData = [...datas];
    newData[seriesIndex] = { ...newData[seriesIndex], [field]: value };
    setDatas(newData);
  };

  const handleDataSeriesBlur = (field, value) => {
    onLogAction?.("series_updated", { seriesIndex, field, value });
  };

  const handleDeleteSeries = () => {
    setDatas((prev) => {
      const nextData = prev.filter((_, index) => index !== seriesIndex);
      onLogAction?.("series_deleted", {
        seriesIndex,
        remainingSeries: nextData.length,
      });
      return nextData;
    });
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (event) => {
      const text = event.target.result;
      const csvDatas = text.replaceAll("\r", "").split("\n");
      const newPoints = csvDatas.filter(Boolean).map((csvData) => {
        const csvSplitData = csvData.split(",");
        return {
          x: parseNumericInput(csvSplitData?.[0] ?? 0),
          y: parseNumericInput(csvSplitData?.[1] ?? 0),
        };
      });
      const newData = [...datas];
      newData[seriesIndex] = { ...newData[seriesIndex], points: newPoints };
      setDatas(newData);
      onLogAction?.("csv_uploaded", {
        seriesIndex,
        pointCount: newPoints.length,
      });
    };
    reader.readAsText(file);
  };

  const handleAddPoint = () => {
    const newData = [...datas];
    const pointData = newData[seriesIndex].points;
    pointData.push({ x: "", y: "" });
    newData[seriesIndex] = { ...newData[seriesIndex], points: pointData };
    setDatas(newData);
    onLogAction?.("point_added", { seriesIndex, pointCount: pointData.length });
  };

  const toggleIsLine = (newValue)=>{
    const newData = [...datas];
    const pointData = newData[seriesIndex].points;
    newData[seriesIndex] = { ...newData[seriesIndex], isLine: newValue };
    setDatas(newData);
    onLogAction?.("togleLine", {});
  }
  return (
    <div style={sectionCardStyle}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 8,
          alignItems: "center",
        }}
      >
        <div style={{ fontWeight: 600 }}>
          {data.name || `系列${seriesIndex + 1}`}
        </div>
        <button onClick={handleDeleteSeries}>系列を削除</button>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <div style={fieldGroupStyle}>
          <div>系列名</div>
          <input
            type="text"
            value={data.name}
            onChange={(e) => handleDataSeriesChange("name", e.target.value)}
            onBlur={(e) => handleDataSeriesBlur("name", e.target.value)}
          />
        </div>
        <div style={fieldGroupStyle}>
          <div>色</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="color"
              value={`#${(data.color || "000000").replace(/^#/, "")}`}
              onChange={(e) =>
                handleDataSeriesChange("color", e.target.value.replace("#", ""))
              }
              onBlur={(e) =>
                handleDataSeriesBlur("color", e.target.value.replace("#", ""))
              }
              style={{
                width: 30,
                height: 24,
                padding: 0,
                border: "none",
                background: "transparent",
                cursor: "pointer",
              }}
            />
            <input
              type="text"
              value={data.color}
              onChange={(e) =>
                handleDataSeriesChange("color", e.target.value.replace("#", ""))
              }
              onBlur={(e) =>
                handleDataSeriesBlur("color", e.target.value.replace("#", ""))
              }
              placeholder="000000"
              style={{ flex: 1 }}
            />
          </div>
        </div>
      </div>
      <div>
        <input
          type="checkBox"
          style={{ marginRight: 10 }}
          checked={data.isLine}
          onClick = {(e)=>toggleIsLine(e.target.checked)}
        />
        近似直線を描画
      </div>
      <div style={sectionStyle}>
        <div style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
              alignItems: "center",
            }}
          >
            <div>データ点</div>
            <input type="file" accept=".csv" onChange={handleCSVUpload} />
          </div>
          {data.points.map((p, j) => (
            <DataPointRow
              key={j}
              point={p}
              pointIndex={j}
              dataIndex={seriesIndex}
              datas={datas}
              setDatas={setDatas}
              onLogAction={onLogAction}
            />
          ))}
          <button style={buttonStyle} onClick={handleAddPoint}>
            データを追加
          </button>
        </div>
      </div>
    </div>
  );
}
